import { Box, Button, FormControl, FormHelperText } from '@mui/material';
import React from 'react';
import { StyledTextField } from '../productInformation';
import { useTranslation } from 'react-i18next';
import {
  getProductComparableFieldValue,
  useProductFieldError,
} from '../validation';
import {
  isValidDecimalInput,
  parseNullableDecimal,
} from 'src/Lca/utils/number';
import { ComplianceField } from 'src/Lca/compliance';

interface Material {
  material_name: string;
  percentage: number;
  recycled_share_pct: number;
  is_basic: boolean;
  id?: number;
  tempId?: number;
}

interface Props {
  values: {
    regulatory_frameworks: string;
    functional_unit: string;
    data_collection_period: string;
    weight: number | null;
    weight_unit: string;
    dimensions: string;
    dimensions_unit: string;
    volume: number | null;
    volume_unit: string;
    production_process_description: string;
    assumptions_description: string;
    reference_service_life: string;
    cutoff_rules: string;
    additional_information: string;
    materials: Material[];
  };
  setValues: Function;
}

export default function Materials({ setValues, values }: Props) {
  const { t } = useTranslation('lca', { keyPrefix: 'materials' });
  const [inputValues, setInputValues] = React.useState<Record<string, string>>(
    {}
  );
  const materialsError = useProductFieldError(
    'materials',
    getProductComparableFieldValue(values as any, 'materials')
  );

  const getInputKey = (
    materialId: number,
    isBasic: boolean,
    type: 'percentage' | 'recycled_share_pct'
  ) => `${materialId}:${isBasic ? 'basic' : 'additional'}:${type}`;

  const handleMaterialChange = (
    materialId: number,
    type: 'material_name' | 'percentage' | 'recycled_share_pct',
    value: string | number,
    isBasic: boolean
  ) => {
    const updatedMaterials = values.materials.map((material) => {
      const _id = material.id || material.tempId;
      return _id === materialId && material.is_basic === isBasic
        ? { ...material, [type]: value }
        : material;
    });
    setValues({ ...values, materials: updatedMaterials });
  };

  const handleRemoveMaterial = (materialId: number, isBasic: boolean) => {
    const updatedMaterials = values.materials.filter((material) => {
      const _id = material.id || material.tempId;
      return !(_id === materialId && material.is_basic === isBasic);
    });
    setValues({ ...values, materials: updatedMaterials });
  };

  const addMaterial = (isBasic: boolean) => {
    const newMaterial: Material = {
      tempId: new Date().getTime(),
      material_name: '',
      percentage: 0,
      recycled_share_pct: 0,
      is_basic: isBasic,
    };
    const updatedMaterials = [...values.materials, newMaterial];
    setValues({ ...values, materials: updatedMaterials });
  };

  const renderMaterialFields = (isBasic: boolean) =>
    values.materials
      .filter((material) => material.is_basic === isBasic)
      .map((material) => {
        const materialId =
          material.id || material.tempId || new Date().getTime();
        const percentageInputKey = getInputKey(
          materialId,
          isBasic,
          'percentage'
        );
        const recycledShareInputKey = getInputKey(
          materialId,
          isBasic,
          'recycled_share_pct'
        );

        return (
          <Box
            sx={{
              display: 'grid',
              gap: '24px',
              alignItems: 'center',
              gridTemplateColumns: {
                xs: '1fr',
                lg: 'minmax(320px, 2fr) minmax(180px, 1fr) minmax(180px, 1fr) auto',
              },
            }}
            key={material.id || material.tempId}
          >
            <Box sx={{ minWidth: 0 }}>
              <ComplianceField
                excelRow={isBasic ? 131 : 134}
                regulatoryFrameworks={values.regulatory_frameworks}
              >
                <FormControl fullWidth>
                  <StyledTextField
                    value={material.material_name}
                    label={
                      isBasic
                        ? t('basicMaterial')
                        : t('optionalAdditionalMaterial')
                    }
                    onChange={(e) => {
                      const { value } = e.target;
                      handleMaterialChange(
                        materialId,
                        'material_name',
                        value,
                        isBasic
                      );
                    }}
                  />
                </FormControl>
              </ComplianceField>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <ComplianceField
                excelRow={isBasic ? 132 : 135}
                regulatoryFrameworks={values.regulatory_frameworks}
              >
                <StyledTextField
                  value={
                    inputValues[percentageInputKey] ??
                    String(material.percentage ?? '')
                  }
                  label={t('materialPercentage')}
                  onChange={(e) => {
                    const { value } = e.target;
                    if (!isValidDecimalInput(value)) {
                      return;
                    }

                    setInputValues((prevValues) => ({
                      ...prevValues,
                      [percentageInputKey]: value,
                    }));
                    handleMaterialChange(
                      materialId,
                      'percentage',
                      parseNullableDecimal(value) ?? 0,
                      isBasic
                    );
                  }}
                  inputProps={{ inputMode: 'decimal' }}
                />
              </ComplianceField>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <ComplianceField
                excelRow={isBasic ? 133 : 136}
                regulatoryFrameworks={values.regulatory_frameworks}
              >
                <StyledTextField
                  value={
                    inputValues[recycledShareInputKey] ??
                    String(material.recycled_share_pct ?? 0)
                  }
                  label={t('recycledSharePercentage')}
                  onChange={(e) => {
                    const { value } = e.target;
                    if (!isValidDecimalInput(value)) {
                      return;
                    }

                    setInputValues((prevValues) => ({
                      ...prevValues,
                      [recycledShareInputKey]: value,
                    }));
                    handleMaterialChange(
                      materialId,
                      'recycled_share_pct',
                      parseNullableDecimal(value) ?? 0,
                      isBasic
                    );
                  }}
                  inputProps={{ inputMode: 'decimal' }}
                />
              </ComplianceField>
            </Box>
            <Box sx={{ justifySelf: { xs: 'stretch', lg: 'start' } }}>
              <Button
                variant="secondary"
                size="small"
                onClick={() => handleRemoveMaterial(materialId, isBasic)}
              >
                {t('remove')}
              </Button>
            </Box>
          </Box>
        );
      });

  return (
    <div style={{ display: 'flex', gap: '24px', flexDirection: 'column' }}>
      {materialsError && (
        <FormHelperText error sx={{ mt: -1 }}>
          {materialsError}
        </FormHelperText>
      )}
      <div style={{ display: 'flex', gap: '24px', flexDirection: 'column' }}>
        {renderMaterialFields(true)}
        <div>
          <Button
            variant="secondary"
            size="small"
            onClick={() => addMaterial(true)}
          >
            {t('addBasicMaterial')}
          </Button>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '24px', flexDirection: 'column' }}>
        {renderMaterialFields(false)}
        <div>
          <Button
            variant="secondary"
            size="small"
            onClick={() => addMaterial(false)}
          >
            {t('addAdditionalMaterial')}
          </Button>
        </div>
      </div>
    </div>
  );
}
